import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, LoaderCircle } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { identityChangeView, type IdentityIntent, type IdentityStatus } from "../lib/commercial-identity";

const key = "vicnet:identity-change";
const eventName = "vicnet:identity-change-start";
function stored(): IdentityIntent | null {
  try { const v = JSON.parse(sessionStorage.getItem(key) ?? "null"); return v?.requestId && v?.values && typeof v.startedAt === "number" ? v : null; } catch { return null; }
}
export function startIdentityChange(intent: IdentityIntent) {
  if (stored()) return;
  sessionStorage.setItem(key, JSON.stringify(intent));
  window.dispatchEvent(new CustomEvent(eventName, { detail: intent }));
}
export function CommercialIdentityGate({ children }: { children: ReactNode }) {
  const [intent, setIntent] = useState(stored);
  const [requestError, setRequestError] = useState<{ message: string; definitive: boolean } | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [canLeave, setCanLeave] = useState(false);
  const sending = useRef(false);
  const client = useQueryClient();
  const query = useQuery({ queryKey: ["commercial-identity-progress"], queryFn: () => api<IdentityStatus>(`/system/commercial-identity?fresh=${Date.now()}`, { cache: "no-store" }), enabled: Boolean(intent), refetchInterval: intent ? 2000 : false, refetchIntervalInBackground: true, retry: false });
  const fresh = !query.isError && Boolean(intent && query.dataUpdatedAt >= intent.startedAt);
  const view = intent ? identityChangeView(intent, query.data, fresh) : null;
  const accessError = query.error instanceof ApiError && [401, 403].includes(query.error.status);
  const finished = Boolean(view?.finished || requestError?.definitive || accessError);
  const label = accessError ? "Ingresá nuevamente para consultar el estado" : requestError?.definitive ? requestError.message : view?.label;
  useEffect(() => {
    const handler = async (event: Event) => {
      const next = (event as CustomEvent<IdentityIntent>).detail;
      setIntent(next); setHistory([]); setRequestError(null);
      if (sending.current) return;
      sending.current = true;
      try { await api("/system/commercial-identity", { method: "POST", body: JSON.stringify({ requestId: next.requestId, values: next.values, expectedRevision: next.expectedRevision }) }); }
      catch (error) {
        if (error instanceof ApiError) setRequestError({ message: error.message, definitive: [400, 401, 403, 409, 422].includes(error.status) });
      } finally { sending.current = false; void client.invalidateQueries({ queryKey: ["commercial-identity-progress"] }); }
    };
    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [client]);
  useEffect(() => {
    if (!intent || !label) return;
    setHistory(previous => previous.at(-1)?.endsWith(label) ? previous : [...previous, `${new Date().toLocaleTimeString()} — ${label}`]);
  }, [intent, label]);
  useEffect(() => {
    if (!intent) return;
    const old = document.body.style.overflow; document.body.style.overflow = "hidden";
    const prevent = (event: BeforeUnloadEvent) => { if (!finished) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", prevent);
    const timer = window.setTimeout(() => setCanLeave(true), 30_000);
    return () => { document.body.style.overflow = old; window.removeEventListener("beforeunload", prevent); window.clearTimeout(timer); };
  }, [intent, finished]);
  function close() { sessionStorage.removeItem(key); setIntent(null); setRequestError(null); setCanLeave(false); void client.invalidateQueries(); }
  return <><div inert={intent ? true : undefined} aria-hidden={intent ? true : undefined}>{children}</div>{intent && <div className="update-lock" role="dialog" aria-modal="true" aria-labelledby="identity-progress-title"><section className="panel update-lock-card">
    {view?.complete ? <CheckCircle2 size={36} /> : finished ? <AlertTriangle size={36} /> : <LoaderCircle size={36} className="spin" />}
    <h1 id="identity-progress-title">{view?.complete ? "Identidad comercial aplicada" : finished ? "Revisá el cambio de configuración" : "Aplicando identidad comercial"}</h1>
    <p role="status" aria-live="polite">{label}</p>
    {!finished && <><p>Se reiniciará la aplicación y puede dejar de responder temporalmente. Comprobamos el regreso automáticamente. No vuelvas a guardar los cambios.</p><progress aria-label="Aplicación de configuración" /></>}
    {view?.error && <p role="alert">{view.error}</p>}
    {requestError && !requestError.definitive && <p>No pudimos confirmar la solicitud. Seguimos consultando su estado sin repetirla.</p>}
    <details open><summary>Progreso</summary><ol>{history.map((line, i) => <li key={i}>{line}</li>)}</ol></details>
    {view?.complete && <p>La aplicación volvió a responder con la nueva identidad. Los mensajes ya generados conservan su contenido anterior.</p>}
    {(finished || canLeave) && <button className="primary" onClick={close}>{finished ? "Volver a la aplicación" : "Volver sin reenviar la solicitud"}</button>}
    {!finished && canLeave && <p>El estado todavía no está confirmado. Volver no cancela la operación del servidor.</p>}
  </section></div>}</>;
}
